import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  MenuItem,
  Paper,
  Stack,
  TextField,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { Controller, useForm } from "react-hook-form";
import DashboardPageHeader from "components/common/DashboardPageHeader";
import { useAuth } from "hooks/useAuth";
import { useOrgAdminAccess } from "hooks/useOrgAdminAccess";
import { completeInstitutionSetup } from "services/fde/institutionSetupService";
import { INSTITUTION_TYPES } from "constants/institutionTypes";

const InstitutionSetupPage = () => {
  const navigate = useNavigate();
  const { userData, organizationId } = useAuth();
  const { canAccess } = useOrgAdminAccess();
  const institutionId =
    userData?.activeInstitutionId ||
    userData?.institutionId ||
    userData?.organizationId ||
    organizationId ||
    null;
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const { control, handleSubmit, formState: { isSubmitting } } = useForm({
    defaultValues: {
      institutionName: "",
      institutionType: "health-system",
      regionId: "",
      subInstitutions: "",
    },
  });

  const onSubmit = async (values) => {
    setErrorMessage("");
    setSuccessMessage("");
    try {
      if (!institutionId) {
        throw new Error("Institution context is missing. Claim your CEO invite first.");
      }

      await completeInstitutionSetup({
        institutionId,
        institutionName: values.institutionName,
        institutionType: values.institutionType,
        regionId: values.regionId,
        subInstitutions: values.subInstitutions,
      });

      setSuccessMessage("Institution setup saved. You can now invite leadership from the role roster.");
      navigate("/dashboard/fde-roster");
    } catch (err) {
      setErrorMessage(
        err?.message ||
          err?.details ||
          "Failed to save institution setup.",
      );
    }
  };

  if (!canAccess) {
    return (
      <Alert severity="warning">
        Institution setup is available to organization admins only.
      </Alert>
    );
  }

  return (
    <Box sx={{ maxWidth: 720, mx: "auto", p: 3 }}>
      <DashboardPageHeader
        title="Institution setup"
        subtitle="Complete your institution profile to begin the invitation cascade."
      />
      {errorMessage ? (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErrorMessage("")}>
          {errorMessage}
        </Alert>
      ) : null}
      {successMessage ? (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage("")}>
          {successMessage}
        </Alert>
      ) : null}
      <Paper variant="outlined" sx={{ p: 3, mt: 2 }}>
        <Stack component="form" spacing={2} onSubmit={handleSubmit(onSubmit)}>
          <Controller
            name="institutionName"
            control={control}
            rules={{ required: "Institution name is required." }}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                label="Institution name"
                error={Boolean(fieldState.error)}
                helperText={fieldState.error?.message}
              />
            )}
          />
          <Controller
            name="institutionType"
            control={control}
            render={({ field }) => (
              <TextField {...field} select label="Institution type">
                {INSTITUTION_TYPES.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.label}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />
          <Controller
            name="regionId"
            control={control}
            render={({ field }) => <TextField {...field} label="Primary region ID" />}
          />
          <Controller
            name="subInstitutions"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Initial sub-institutions"
                multiline
                minRows={3}
                helperText="One name per line"
              />
            )}
          />
          <LoadingButton type="submit" variant="contained" loading={isSubmitting}>
            Save and continue
          </LoadingButton>
        </Stack>
      </Paper>
    </Box>
  );
};

export default InstitutionSetupPage;
